import type { RefObject } from 'react'

import { useEffect, useLayoutEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// The reveal choreography the detail view uses, re-pointed at scroll position
// instead of an open event:
//   · [data-title]  — a masked serif line riding up from below (yPercent 120 → 0)
//   · [data-rev]    — everything else fading up 24px on a power4.out
//   · [data-group]  — the block a set of [data-rev] items staggers inside
//   · [data-delay]  — optional hold, in seconds, before that item plays
//
// The hidden state is applied HERE, not in CSS. That ordering matters: if the
// stylesheet hides the content and this hook fails to run for any reason, the
// page keeps its text invisible for good. Owning both halves means the worst
// case is an un-animated page rather than an empty one. A layout effect runs
// before the browser paints, so hiding here still costs no flash.

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const readDelay = (el: HTMLElement): number => {
    const raw = el.dataset.delay
    if (!raw) return 0
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
}

export const useReveal = (rootRef: RefObject<HTMLElement | null>): void => {
    useIsomorphicLayoutEffect(() => {
        const root = rootRef.current
        if (!root) return

        const revs = Array.from(root.querySelectorAll<HTMLElement>('[data-rev]'))
        const titles = Array.from(root.querySelectorAll<HTMLElement>('[data-title]'))

        // Nothing to hide, nothing to play.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        gsap.registerPlugin(ScrollTrigger)

        // Anything already on screen when the page opens plays straight away —
        // a hero has nothing to scroll into, so gating it on a scroll trigger
        // would leave it hidden until the viewer moved.
        const inView = (el: Element): boolean => {
            const r = el.getBoundingClientRect()
            return r.top < window.innerHeight * 0.92 && r.bottom > 0
        }

        const ctx = gsap.context(() => {
            titles.forEach((el) => {
                const mask = el.parentElement ?? el

                const from = { yPercent: 120 }
                const to = {
                    yPercent: 0,
                    duration: 0.9,
                    ease: 'power4.out',
                    delay: readDelay(el)
                }

                if (inView(mask)) {
                    gsap.fromTo(el, from, to)
                    return
                }

                gsap.fromTo(el, from, {
                    ...to,
                    // the span is translated down, so measure its mask instead
                    scrollTrigger: { trigger: mask, start: 'top 92%', once: true }
                })
            })

            // Items stagger together inside their nearest [data-group] block.
            const groups = new Map<Element, HTMLElement[]>()

            revs.forEach((el) => {
                const key = el.closest('[data-group]') ?? root
                const bucket = groups.get(key)
                if (bucket) bucket.push(el)
                else groups.set(key, [el])
            })

            groups.forEach((items, key) => {
                const from = { opacity: 0, y: 24 }
                const to = {
                    opacity: 1,
                    y: 0,
                    duration: 0.7,
                    ease: 'power4.out',
                    stagger: 0.08,
                    delay: readDelay(items[0])
                }

                if (inView(key)) {
                    gsap.fromTo(items, from, to)
                    return
                }

                gsap.fromTo(items, from, {
                    ...to,
                    scrollTrigger: { trigger: key, start: 'top 88%', once: true }
                })
            })
        }, root)

        return () => ctx.revert()
    }, [rootRef])
}
